/**
 * Résumé « inventaire manuel vs auto (RFID CPI) » d'une boutique `cpi_inventory_enabled`
 * (`GET /stores/cpi-inventory-summary`, page BO `/cpi-inventory`, POC Saint-Charles).
 * Une seule définition, partagée par le backend et le client `@gozoki/api`.
 */
export interface CpiInventoryStoreSummary {
  store: { id: number; label: string; checkpointStoreRef: string }
  /** Fraîcheur de la donnée RFID + garde-fou. */
  lastSyncAt: string | null
  cursorAt: string | null
  cpiDown: boolean
  /** État de la fenêtre de remplissage (étape A-bis) : ancre des sorties manuelles et des métriques de lecture. */
  restock: {
    /** Fin de la fenêtre ouverte par le dernier restock ; null = jamais détecté. */
    activeUntil: string | null
    lastDetectedAt: string | null
    /** `true` = remplissage en cours (fenêtre pas encore échue). */
    detectionActive: boolean
    /** EPC distincts lus dans la fenêtre glissante (proxy d'activité de remplissage). */
    recentEpcCount: number
    /** Config effective (override boutique sinon défaut global). */
    config: { epcThreshold: number; windowMinutes: number; detectionHours: number }
  }
  /**
   * Inventaire MANUEL :
   * - `inStock` = stock courant (état, sans borne temporelle).
   * - `inventoryDeleted` / `sold` = SORTIES bornées à la dernière fenêtre de remplissage :
   *   `lost_at >= restock.activeUntil` (fin des 3h = snapshot inventaire auto). Sans restock
   *   connu (`activeUntil` null) → 0. Pendant un remplissage (`activeUntil` futur) → 0 tant que
   *   la fenêtre n'est pas fermée (cf. helper front `restock.detectionActive`).
   */
  manual: { inStock: number; inventoryDeleted: number; sold: number }
  /** 10 derniers remplissages (`cpi_restock_events`) avec leurs métriques de lecture (POC). */
  restockEvents: {
    id: number
    startedAt: string
    activeUntil: string
    closedAt: string | null
    deliveryDate: string
    /** `reappro_needs` du jour de livraison (ce qu'on a envoyé à Luna), par référence. */
    expectedCount: number
    /** EPC distincts lus pendant la fenêtre. */
    readCount: number
    /** Articles scannés en boutique (Checkpoint) le jour de livraison. */
    receivedCount: number
    /** lu / attendu, null si rien d'attendu ce jour. */
    readRate: number | null
  }[]
  /**
   * Inventaire AUTO (RFID temps réel) :
   * - `seenRecently` = IN_STOCK lus dans la fenêtre glissante de remplissage ;
   * - `neverSeen` = IN_STOCK jamais lus (angle mort) ;
   * - `expiredToDispose` = périmés de la semaine pas encore sortis au gun (`disposed_at` nul) ;
   * - `theftsConfirmed` = vols confirmés par l'étape C sur 7 jours (audit `cpi_theft_confirmed`) ;
   * - `legacyPresumed` = reliquat PRESUMED_STOLEN / STOLEN posés par l'ancienne détection par
   *   seuils (retirée le 2026-10-29), reconnus à leur audit `cpi_presumed_stolen` /
   *   `cpi_stolen` : plus rien ne les écrit, seule une relecture les ranime. Un STOLEN de panier
   *   non payé (vol boutique) n'en fait pas partie.
   */
  auto: {
    seenRecently: number
    neverSeen: number
    expiredToDispose: number
    theftsConfirmed: number
    legacyPresumed: number
  }
  /**
   * Derniers vols confirmés (audit `cpi_theft_confirmed`), du plus récent au plus ancien, tronqués
   * à 50 ; le total est `auto.theftsConfirmed`.
   */
  missing: {
    id: number
    epc: string
    productReference: string
    productLabel: string | null
    confirmedAt: string
    lastSeenAt: string | null
    lostAt: string | null
  }[]
}

/**
 * Une référence produit en écart pour l'ADV (`GET /stores/cpi-discrepancies/:id`, POC
 * Saint-Charles) : attendu / lu / reçu sur le dernier remplissage, stock courant, périmés pas
 * encore sortis au gun, vols confirmés sur 7 jours. Une ligne n'existe que si quelque chose cloche.
 */
export interface CpiDiscrepancyLine {
  productReference: string
  productLabel: string | null
  /** `reappro_needs` du jour de livraison du dernier remplissage (0 si aucun). */
  expected: number
  /** EPC distincts lus pendant le dernier remplissage. */
  read: number
  /** Articles reçus au gun (`boutique_at`) le jour de livraison. */
  received: number
  inStock: number
  /** EXPIRED de la semaine sans `disposed_at` : à sortir au gun (zone POUBELLE). */
  expiredToDispose: number
  /** Vols confirmés par l'étape C sur 7 jours, encore INVENTORY_DELETED. */
  theftsConfirmed: number
}

export interface CpiDiscrepancies {
  store: { id: number; label: string }
  /** Dernier remplissage connu (ouvert ou clos), base des colonnes attendu / lu / reçu. */
  restockEvent: { id: number; deliveryDate: string; closedAt: string | null } | null
  lines: CpiDiscrepancyLine[]
}

/** Écart maximal `to − from` d'un bilan POC, en jours : un POC dure 7 à 10 jours, un trimestre laisse de la marge. */
export const CPI_POC_MAX_DAYS = 92

const DAY_MS = 86_400_000

/** Écart `to − from` en jours entre deux jours ISO (`YYYY-MM-DD`) ; NaN si l'un est illisible. */
export const cpiPocPeriodSpanDays = (fromDay: string, toDay: string): number =>
  (Date.parse(`${toDay}T00:00:00Z`) - Date.parse(`${fromDay}T00:00:00Z`)) / DAY_MS

/**
 * Règle unique de la période d'un bilan POC, serveur (422 `maxSpan`) et formulaire BO :
 * `to` ≥ `from`, et au plus `CPI_POC_MAX_DAYS` jours d'écart.
 */
export const isCpiPocPeriodValid = (fromDay: string, toDay: string): boolean => {
  const span = cpiPocPeriodSpanDays(fromDay, toDay)
  return span >= 0 && span <= CPI_POC_MAX_DAYS
}

/**
 * Nom du fichier XLSX du bilan, sans extension, partagé par le `Content-Disposition` du serveur et
 * par le BO (Tuyau en `responseType: 'blob'` ne rend pas les en-têtes).
 */
export const cpiPocXlsxFileName = (storeLabel: string, fromDay: string, toDay: string): string =>
  `poc-rfid_${storeLabel.replace(/[^\w-]+/g, '-')}_${fromDay}_${toDay}`

/**
 * Bilan POC RFID d'une boutique sur une période (brief Baptiste 2026-10-29) : part des vols
 * horodatés (cible ≥ 90 %) et taux de lecture par livraison. Source de l'export XLSX
 * (`GET /stores/cpi-poc-xlsx/:id`).
 */
export interface CpiPocReport {
  store: { id: number; label: string }
  period: { from: string; to: string }
  /**
   * Vols = INVENTORY_DELETED (constatés par un inventaire ou par l'étape C) dont `lost_at` tombe
   * dans la période ; `timestamped` = dernière lecture RFID connue. STOLEN / PRESUMED_STOLEN
   * (reliquat de l'ancienne détection par seuils) n'en font pas partie.
   */
  thefts: { total: number; timestamped: number; rate: number | null }
  /** Parmi ces vols, ceux confirmés par l'étape C (audit `cpi_theft_confirmed`). */
  cpiConfirmed: number
  /** Périmés sortis au gun (`disposed_at`) dans la période, encore EXPIRED. */
  expiredDisposed: number
  /** Remplissages clos dont le début tombe dans la période. */
  restockEvents: {
    deliveryDate: string
    expectedCount: number
    readCount: number
    receivedCount: number
    readRate: number | null
  }[]
  /** Moyenne simple des taux de lecture définis ; null sans livraison mesurable. */
  readRateAverage: number | null
}
